import React, { useContext } from "react";
import { Button, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getDuplicateApps } from "go-app/utils";
import { ReactComponent as InfoCircleSVG } from "go-component/images/svg/info-circle.svg";
import FormatDate, { formatFormDateToString } from "go-core/components/Formatters/FormatDate";
import { ReactComponent as OpenAppSVG } from "go-core/images/svg/actions/link.svg";
import { redirectToApplication } from "go-core/utils/utils";
import AppEntityStatus from "../../../../../../components/Common/EntityStatus/EntityStatus";
import { ReactComponent as TimerSVG } from "../../../../../../images/svg/organization/clock.svg";
import { ReactComponent as CloudArrowDownSVG } from "../../../../../../images/svg/organization/cloud-arrow-down.svg";
import { ReactComponent as FlagSVG } from "../../../../../../images/svg/organization/flag.svg";
import { OrganizationApplicationApi } from "../../../../../../services/Api/types";
import { ConfigContext } from "../../../../../../services/Config/context";
import { getNewAppsVersion } from "../../../../../../utils/AppVersion";
import FormatSubscriptionLicenseType from "../../../FormatSubscriptionLicenseType/FormatSubscriptionLicenseType";
import { RenderApplicationLogo } from "../../../RenderApplicationLogo/RenderApplicationLogo";

interface Props {
	app: OrganizationApplicationApi;
	companyId: string;
}

const OrganizationApplicationCard = ({ app, companyId }: Props) => {
	const { t } = useTranslation("admin");
	const configService = useContext(ConfigContext);
	app.created_at = app.last_used_at;
	const functionsUsed = ["Pyszne.pl", "Wolt", "GoDelivery", "Uber Eats", "Centrala telefoniczna", "Płatności online"];
	const applications = configService.get().apps;
	const duplicatedApps = getDuplicateApps(applications);
	const uniqueApplications = getNewAppsVersion(duplicatedApps, applications);
	const applicationFromConfig = uniqueApplications.find(
		(uniqueApplication) => uniqueApplication.id.toUpperCase() === app.slug
	);
	const appRedirectLink = redirectToApplication(applicationFromConfig?.url ?? "", companyId);

	const getAccessValueTextColor = (value: any) => {
		if (value === "false") return "text-danger";
		if (value === "true") return "text-success";
		return "text-primary";
	};

	const drawAccessValue = (value: any) => {
		if (value === "false") return t("common.word.no", { ns: "lib" });
		if (value === "true") return t("common.word.yes", { ns: "lib" });
		return value;
	};

	const drawLicenseDate = () => {
		const dates: string[] = [];
		if (app.license_created_at) dates.push(FormatDate(app.license_created_at).split(",")[0]);
		if (app.license_expires_at) dates.push(FormatDate(app.license_expires_at).split(",")[0]);
		const isLicenseExpired = new Date().getTime() > formatFormDateToString(app.license_expires_at).getTime();

		return (
			<span className={`${isLicenseExpired ? "text-danger" : "text-success"} label ms-2`}>
				{dates.join(" - ")}
			</span>
		);
	};

	const drawProperty = (property: { type: string; value: any }) => {
		const { type, value } = property;
		if (type === "FIRST_ACTIVITY" || type === "LAST_ACTIVITY" || type === "LAST_CLOSED_REPORT_DATE")
			return FormatDate(value);
		return value;
	};

	return (
		<Card className="app-card">
			<Card.Body>
				<div className="row">
					<div className="col-xl-7">
						<div className="d-flex">
							<div className="app-logo">
								<RenderApplicationLogo slug={app.slug} />
							</div>
							<div className="d-flex flex-grow-1 ms-2 flex-column">
								<div className="d-flex mb-2 align-items-center">
									<h6 className="me-2 mb-0">{app.name}</h6>
									<AppEntityStatus status={app.status} />
									<div className="badge bg-warning text-dark ms-2 align-items-center d-flex px-2">
										<InfoCircleSVG />
										<span className="ms-2">
											{t("modules.organization.field.use_more_functions.title")}
										</span>
									</div>
								</div>
								<div className="row">
									{app.created_at && (
										<div className="d-flex align-items-center me-2">
											<OverlayTrigger
												placement="top"
												overlay={
													<Tooltip id={`tooltip-${app.slug}-created-at`}>
														{t("lib:common.word.created_at")}
													</Tooltip>
												}
											>
												<CloudArrowDownSVG />
											</OverlayTrigger>
											<span className="label text-muted ms-1">
												{FormatDate(app.created_at).split(",")[0]}
											</span>
										</div>
									)}
									{app.activated_at && (
										<div className="d-flex align-items-center me-2">
											<OverlayTrigger
												placement="top"
												overlay={
													<Tooltip id={`tooltip-${app.slug}-activated-at`}>
														{t("common.word.activated_at")}
													</Tooltip>
												}
											>
												<FlagSVG />
											</OverlayTrigger>
											<span className="label text-muted ms-1">
												{FormatDate(app.activated_at).split(",")[0]}
											</span>
										</div>
									)}
									{app.last_used_at && (
										<div className="d-flex align-items-center">
											<OverlayTrigger
												placement="top"
												overlay={
													<Tooltip id={`tooltip-${app.slug}-last-used-at`}>
														{t("common.word.last_used_at")}
													</Tooltip>
												}
											>
												<TimerSVG />
											</OverlayTrigger>
											<span className="label text-muted ms-1">
												{FormatDate(app.last_used_at).split(",")[0]}
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<div className="col-xl-5 text-xl-right mt-3 mt-xl-0">
						<Button variant="add">
							<a target="_blank" href={appRedirectLink} rel="noreferrer">
								{t("modules.organization.action.pass_to_application.title")}
								<OpenAppSVG style={{ marginLeft: "8px" }} />
							</a>
						</Button>
					</div>
				</div>
				<div className="row mt-3">
					<div className="col-xl-6">
						<div className="d-flex flex-column">
							<span className="text-muted label">{t("common.word.license")}</span>
							<div className="d-flex align-items-center mt-1">
								<FormatSubscriptionLicenseType license_type={app.license_type} />
								{drawLicenseDate()}
							</div>
						</div>
						<div className="d-flex flex-column mt-3">
							<span className="text-muted label mb-1">
								{t("modules.organization.field.functions_used.title")}
							</span>
							<div className="d-flex flex-wrap">
								{functionsUsed.map((functionUsed, index) => (
									<div
										key={`function-used-${index}`}
										className={`barcode-label bg-light ${
											index === functionsUsed?.length - 1 ? "me-0" : "me-2"
										}`}
									>
										{functionUsed}
									</div>
								))}
							</div>
						</div>
					</div>
					<div className="col-xl-6">
						<span className="text-muted label mb-1">{t("modules.organization.field.stats.title")}</span>
						{app.properties && app.properties.length > 0 && (
							<div className="d-flex flex-column">
								{app.properties.map((prop) => {
									if (t(`enums.app_properties.type.${prop.type}`).startsWith("enums")) return null;

									return (
										<div
											key={prop.type}
											className="barcode-label d-block bg-light text-muted p-0 h-auto"
										>
											<span className="m-0">{t(`enums.app_properties.type.${prop.type}`)}:</span>
											<span className="text-primary">{drawProperty(prop)}</span>
										</div>
									);
								})}
							</div>
						)}
						{app.accesses && app.accesses.length > 0 && (
							<div className="d-flex flex-column">
								{app.accesses.map((access) => {
									if (t(`enums.accesses.type.${access.type}`).startsWith("enums")) return null;

									return (
										<div
											className="barcode-label d-block bg-light text-muted p-0 h-auto"
											key={access.type}
										>
											<span className="m-0">{t(`enums.accesses.type.${access.type}`)}:</span>
											<span className={getAccessValueTextColor(access.value)}>
												{drawAccessValue(access.value)}
											</span>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
export default OrganizationApplicationCard;
